import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { Card, Label, MonoMeta, SummaryStripBase } from "@/design/primitives";
import { EmailZeroTile } from "./EmailZeroTile";

// The zero-inbox wall (/email/board). One wall, one number, and every piece
// of the number is visible:
//
//   headline (two tiers)  -> the clean count, or the healthy "nothing waiting"
//                            line, or the "nothing new, older ones wait" line
//   burst line            -> the caught burst, with the count the filter took
//   brand tiles           -> one per brand
//   unsorted tile         -> always shown, always counted
//   waiting-longer strip  -> 31-90 day items, adjacent to the headline
//   waiting-old strip     -> 91+ day unanswered items, still on the board
//   archive pool line     -> how many sit parked, by the archive predicate
//
// The stamp carries the weekday, the date and the time, and a behind feed
// says so in plain words. Nothing here is a colored bar, a dark background,
// or a dot standing in for progress.

const SEPARATOR = " · ";

// The stamp: weekday + date + time, and the honest note when the feed is behind.
const FreshnessStamp = ({ stamp }) => {
  const dead = stamp.dead;
  const fresh = stamp.fresh !== false;
  const behind = !fresh || dead;
  return (
    <MonoMeta
      as="p"
      data-testid="email-zero-as-of"
      data-fresh={fresh}
      data-dead={dead}
      className={cn(
        "shrink-0 whitespace-nowrap pt-1",
        dead ? "text-os-red" : fresh ? "text-os-ink" : "text-os-amber",
      )}
    >
      count from {stamp.as_of_display}
      {dead ? " — board stale" : fresh ? "" : " — feed behind"}
    </MonoMeta>
  );
};

// The headline, two tiers. The healthy "nothing waiting" line is only ever
// rendered when nothing at all is waiting — a behind feed never claims zero.
const Headline = ({ stamp }) => {
  const clean = stamp.clean_total ?? 0;
  const longer = stamp.waiting_longer_total ?? 0;
  const old = stamp.old_total ?? 0;
  const behind = stamp.fresh === false || stamp.dead;

  if (clean > 0) {
    return (
      <Card quiet className="px-5 py-3" data-testid="email-zero-headline" data-tier="count">
        <p className="text-[1.125rem] font-medium text-os-ink">
          {stamp.headline?.label || `${clean} need a reply`}
        </p>
        {behind && (
          <p className="mt-1 text-[0.875rem] text-os-amber">
            Count from {stamp.as_of_display} — feed behind
          </p>
        )}
      </Card>
    );
  }

  if (longer > 0 || old > 0) {
    return (
      <Card quiet className="border-os-red px-5 py-3" data-testid="email-zero-headline" data-tier="older">
        <p className="text-[1.125rem] font-medium text-os-ink">Nothing new waiting on you</p>
        <p className="mt-1 text-[0.875rem] text-os-red">
          {longer + old} are still open and older than 30 days
        </p>
        {behind && (
          <p className="mt-1 text-[0.875rem] text-os-amber">
            Count from {stamp.as_of_display} — feed behind
          </p>
        )}
      </Card>
    );
  }

  // Zero everything — but only say so when the feed is healthy. A behind feed
  // never claims an empty inbox.
  if (behind) {
    return (
      <Card quiet className="border-os-amber px-5 py-3" data-testid="email-zero-headline" data-tier="behind">
        <p className="text-[1.125rem] font-medium text-os-ink">
          Count from {stamp.as_of_display} — feed behind
        </p>
      </Card>
    );
  }

  return (
    <Card quiet className="px-5 py-3" data-testid="email-zero-headline" data-tier="zero">
      <p className="text-[1.125rem] font-medium text-os-green">Inbox zero — nothing waiting on you</p>
    </Card>
  );
};

// The caught-burst line. Its count is exactly the rows the filter took out,
// and it hands the call back to Jeff in his own words.
const BurstLine = ({ burst }) => {
  if (!burst || !burst.brands?.length) return null;
  return (
    <Card quiet className="border-os-amber px-5 py-3" data-testid="email-zero-burst" data-excluded={burst.excluded}>
      <Label as="p">{burst.label}</Label>
      <p className="mt-1.5 text-[0.875rem] leading-[1.45] text-os-ink-2">{burst.note}</p>
      {burst.brands.map((b) => (
        <p
          key={`${b.brand}-${b.kind}`}
          data-testid={`email-zero-burst-${b.brand}-${b.kind}`}
          className="mt-1 text-[0.8125rem] leading-[1.4] text-os-ink-3"
        >
          {b.tile}: {b.note}
        </p>
      ))}
    </Card>
  );
};

// The waiting-longer strip (31-90 days) and the waiting-old strip (91+ days).
// Both sit next to the headline, never inside the clean count, never hidden.
const WaitingStrips = ({ stamp }) => {
  const longer = stamp.waiting_longer_total ?? 0;
  const old = stamp.old_total ?? 0;
  if (!longer && !old) return null;
  return (
    <>
      {longer > 0 && (
        <Card quiet className="px-5 py-3" data-testid="email-zero-waiting-longer">
          <p className="text-[0.875rem] leading-[1.45] text-os-amber">
            {longer} still open — waiting longer
          </p>
        </Card>
      )}
      {old > 0 && (
        <Card quiet className="border-os-red px-5 py-3" data-testid="email-zero-waiting-old">
          <p className="text-[0.875rem] leading-[1.45] text-os-red">
            {old} waiting — old
          </p>
        </Card>
      )}
    </>
  );
};

// The archive pool count line — how many sit parked, and where they came from.
const PoolLine = ({ stamp }) => {
  const count = stamp.archive_count ?? 0;
  return (
    <Card quiet className="px-5 py-3" data-testid="email-zero-pool">
      <p className="text-[0.875rem] leading-[1.45] text-os-ink-2">
        {count} left parked — archive candidate
      </p>
    </Card>
  );
};

export const EmailZeroBoard = ({ cards, stamp, variant = "desktop" }) => {
  const desktop = variant === "desktop";

  const brandCards = useMemo(
    () => (cards || []).filter((c) => c.slug?.startsWith("brand:") && c.slug !== "brand:unfiled"),
    [cards],
  );
  const unfiledCard = useMemo(
    () => (cards || []).find((c) => c.slug === "brand:unfiled"),
    [cards],
  );

  const parts = [];
  const clean = stamp?.clean_total ?? 0;
  const longer = stamp?.waiting_longer_total ?? 0;
  const old = stamp?.old_total ?? 0;
  if (clean > 0) parts.push({ key: "clean", text: `${clean} need a reply`, tone: "red" });
  else parts.push({ key: "clean", text: "0 need a reply", tone: "green" });
  if (longer > 0) parts.push({ key: "longer", text: `${longer} waiting longer`, tone: "amber" });
  if (old > 0) parts.push({ key: "old", text: `${old} waiting — old`, tone: "red" });
  if (!clean && !longer && !old) parts.push({ key: "zero", text: "inbox zero", tone: "green" });

  const tone = stamp?.dead ? "red" : stamp?.fresh === false ? "amber" : clean > 0 ? "red" : longer > 0 || old > 0 ? "amber" : "quiet";

  return (
    <div data-testid="email-zero-board" data-variant={variant} className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        <SummaryStripBase
          testId="email-zero-summary"
          parts={parts}
          tone={tone}
          text={parts.map((p) => p.text).join(SEPARATOR)}
          separator={SEPARATOR}
          size={desktop ? "lg" : "sm"}
          meta={stamp?.as_of_display ? <FreshnessStamp stamp={stamp} /> : null}
        />
        <Headline stamp={stamp || {}} />
        <BurstLine burst={stamp?.burst} />
        <WaitingStrips stamp={stamp || {}} />
        <PoolLine stamp={stamp || {}} />
        <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
          {brandCards.map((card) => (
            <EmailZeroTile key={card.slug} card={card} />
          ))}
        </div>
        {unfiledCard && (
          <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
            <EmailZeroTile card={unfiledCard} />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailZeroBoard;
